import { generatePath, matchPath } from "react-router";
import { ROUTES } from "../../../routes";
import { ObjectUrlName } from "../../../utils/enums";

// The `type` union here mirrors the backend `PAGE_CONTEXT_REGISTRY` in
// `optinode/webserver/ai_chat/page_context.py`. The two drift independently;
// unknown types are dropped on the backend, so adding a type on one side
// before the other is safe (if temporarily lossy).
export interface PageContextRef {
  type: "project" | "site" | "generator";
  id: string;
  name?: string;
}

// Match the canonical route plus any sub-path. `matchPath` requires an explicit
// `*` wildcard suffix to match sub-routes.
const SITE_COMPONENT_ROUTE = `${ROUTES.siteComponentPage}/*`;
const SITE_ROUTE = `${ROUTES.site.entry}*`;
const PROJECT_ROUTE = `${ROUTES.project.entry}*`;

function resolvePageContext(pathname: string): Array<PageContextRef> {
  // The routes are strict nested prefixes (a generator URL contains the site
  // URL, which contains the project URL), so we can accumulate each tier
  // independently: whichever routes match the current path contribute their
  // ref. The push order below MUST stay project -> site -> generator, because
  // `linkForRefs` walks the array from the end and assumes each ref's parent
  // precedes it.
  const context: Array<PageContextRef> = [];

  const projectMatch = matchPath(PROJECT_ROUTE, pathname);
  if (projectMatch?.params.projectId) {
    context.push({ type: "project", id: projectMatch.params.projectId });
  }

  const siteMatch = matchPath(SITE_ROUTE, pathname);
  if (siteMatch?.params.siteId) {
    context.push({ type: "site", id: siteMatch.params.siteId });
  }

  // `:objectUrlName` also covers other component types (consumers, storage,
  // etc.), so only add a generator ref when this is actually a generator page.
  const generatorMatch = matchPath(SITE_COMPONENT_ROUTE, pathname);
  if (
    generatorMatch?.params.objectUrlName === ObjectUrlName.Generators &&
    generatorMatch.params.componentId
  ) {
    context.push({ type: "generator", id: generatorMatch.params.componentId });
  }

  // New tiers MUST be appended here in nesting order (outermost first). Do not
  // insert a push above an existing one — `linkForRefs` reads parents by their
  // fixed offset from the end of the array.
  return context;
}

function isPageContextRef(value: unknown): value is PageContextRef {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    (candidate.type === "project" ||
      candidate.type === "site" ||
      candidate.type === "generator") &&
    typeof candidate.id === "string"
  );
}

function linkForRefs(refs: ReadonlyArray<unknown>): string | null {
  const last = refs[refs.length - 1];
  if (!isPageContextRef(last)) {
    return null;
  }

  if (last.type === "project") {
    return generatePath(ROUTES.project.entry, { projectId: last.id });
  }

  if (last.type === "site") {
    const projectRef = refs[refs.length - 2];
    if (!isPageContextRef(projectRef) || projectRef.type !== "project") {
      return null;
    }

    return generatePath(ROUTES.site.entry, {
      projectId: projectRef.id,
      siteId: last.id
    });
  }

  if (last.type === "generator") {
    const siteRef = refs[refs.length - 2];
    const projectRef = refs[refs.length - 3];

    if (
      !isPageContextRef(siteRef) ||
      siteRef.type !== "site" ||
      !isPageContextRef(projectRef) ||
      projectRef.type !== "project"
    ) {
      return null;
    }

    return generatePath(ROUTES.siteComponentPage, {
      projectId: projectRef.id,
      siteId: siteRef.id,
      objectUrlName: ObjectUrlName.Generators,
      componentId: last.id
    });
  }

  return null;
}

export { linkForRefs, resolvePageContext };
